import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { CASES, TIER_META, fmtUsd } from "../../data/casesData";
import { useAuth } from "../../context/AuthContext";
import { useCredit } from "../../context/CreditContext";
import { useToast } from "../../hooks/use-toast";
import { Button } from "../ui/button";
import ItemImage from "./ItemImage";
import WithdrawModal from "../WithdrawModal";
import { Package, DollarSign, Truck, PackageOpen, Loader2, LogIn, Check, CheckSquare, Square } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => {
  const t = localStorage.getItem("uc_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const ICON_MAP = {};
CASES.forEach((c) => c.items.forEach((i) => { ICON_MAP[i.name] = i.icon; }));

export default function InventoryGrid() {
  const { user } = useAuth();
  const { refreshCredits } = useCredit();
  const { toast } = useToast();
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [sellingBatch, setSellingBatch] = useState(false);

  const load = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    try {
      const { data } = await axios.get(`${API}/inventory`, { headers: authHeader() });
      setInventory(data.inventory || []);
    } catch (_) {} finally { setLoading(false); }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const isSellable = (it) => it.status === "owned" || it.status == null;
  const sellableItems = useMemo(() => inventory.filter(isSellable), [inventory]);
  const selectedItems = useMemo(() => inventory.filter((it) => selected.has(it.id)), [inventory, selected]);
  const selectedTotal = useMemo(() => selectedItems.reduce((s, it) => s + Number(it.value || 0), 0), [selectedItems]);
  const allSelected = sellableItems.length > 0 && selectedItems.length === sellableItems.length;

  const toggleOne = (id) => setSelected((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const toggleAll = () => setSelected(() => (allSelected ? new Set() : new Set(sellableItems.map((it) => it.id))));

  const act = async (iid, kind) => {
    try {
      await axios.post(`${API}/inventory/${iid}/${kind}`, {}, { headers: authHeader() });
      if (kind === "sell") {
        setInventory((inv) => inv.filter((x) => x.id !== iid));
        setSelected((prev) => { const n = new Set(prev); n.delete(iid); return n; });
        refreshCredits && refreshCredits();
      }
    } catch (_) {}
  };

  const sellSelected = async () => {
    const ids = [...selected].filter((id) => sellableItems.some((it) => it.id === id));
    if (!ids.length) return;
    setSellingBatch(true);
    try {
      const { data } = await axios.post(`${API}/inventory/sell/batch`, { item_ids: ids }, { headers: authHeader() });
      const soldIds = new Set(data.sold_ids || ids);
      setInventory((inv) => inv.filter((x) => !soldIds.has(x.id)));
      setSelected(new Set());
      refreshCredits && refreshCredits();
      toast({ title: `Sold ${data.sold} item${data.sold !== 1 ? "s" : ""}`, description: `+${fmtUsd(data.payout)} credit` });
    } catch (e) {
      toast({ title: "Couldn't sell", description: e?.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally { setSellingBatch(false); }
  };

  const [shipTarget, setShipTarget] = useState(null);

  if (!user) {
    return (
      <div className="rounded-lg border border-[#2A2440] dark:border-[#2A2440] bg-[#19142A] dark:bg-[#19142A] p-10 text-center shadow-sm" data-testid="inventory-auth-gate">
        <LogIn className="h-10 w-10 text-[#C4C9D4] mx-auto mb-3" />
        <div className="font-cases uppercase text-white dark:text-white">Sign in to view your inventory</div>
        <div className="text-sm text-[#8A90A0]">Open cases or win battles to collect items here.</div>
      </div>
    );
  }

  if (loading) {
    return <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#8A90A0]" /></div>;
  }

  if (!inventory.length) {
    return (
      <div className="rounded-lg border border-[#2A2440] dark:border-[#2A2440] bg-[#19142A] dark:bg-[#19142A] p-10 text-center shadow-sm" data-testid="inventory-empty">
        <PackageOpen className="h-10 w-10 text-[#C4C9D4] mx-auto mb-3" />
        <div className="font-cases uppercase text-white dark:text-white">Your inventory is empty</div>
        <div className="text-sm text-[#8A90A0]">Open a case or win a battle and the items land here.</div>
      </div>
    );
  }

  return (
    <>
    {sellableItems.length > 0 && (
      <div data-testid="inventory-action-bar" className="sticky top-0 z-10 -mx-1 mb-3 flex items-center justify-between gap-2 bg-white/95 dark:bg-[#141021]/95 backdrop-blur px-1 py-2 border-b border-[#2A2440] dark:border-[#2A2440]">
        <button data-testid="inventory-select-all" onClick={toggleAll}
          className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-white dark:text-white hover:opacity-80 transition-opacity">
          {allSelected ? <CheckSquare className="h-4 w-4 text-[#16A34A]" /> : <Square className="h-4 w-4 text-[#9AA0AE]" />}
          {allSelected ? "Deselect all" : "Select all"}
          <span className="text-[#9AA0AE] font-medium normal-case">({selectedItems.length}/{sellableItems.length})</span>
        </button>
        <Button data-testid="inventory-sell-selected-btn" size="sm" disabled={!selectedItems.length || sellingBatch}
          onClick={sellSelected}
          className="h-8 text-xs rounded-lg bg-[#16A34A] hover:bg-[#15903f] text-white gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed">
          {sellingBatch ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <DollarSign className="h-3.5 w-3.5" />}
          Sell selected{selectedItems.length ? ` · ${fmtUsd(selectedTotal)}` : ""}
        </Button>
      </div>
    )}
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="inventory-grid">
      {inventory.map((it) => {
        const meta = TIER_META[it.tier] || TIER_META.common;
        const Icon = ICON_MAP[it.name] || Package;
        const sellable = isSellable(it);
        const checked = selected.has(it.id);
        return (
          <div key={it.id} data-testid="inventory-item"
            className={`relative rounded-lg border bg-[#19142A] dark:bg-[#19142A] overflow-hidden shadow-sm transition-colors ${checked ? "border-[#16A34A] ring-1 ring-[#16A34A]" : "border-[#2A2440] dark:border-[#2A2440]"}`}>
            <div className="h-1 w-full" style={{ background: meta.color }} />
            {sellable && (
              <button data-testid="inventory-item-checkbox" onClick={() => toggleOne(it.id)} aria-pressed={checked}
                className={`absolute top-2 left-2 z-10 h-5 w-5 rounded flex items-center justify-center border transition-colors ${checked ? "bg-[#16A34A] border-[#16A34A] text-white" : "bg-white/90 dark:bg-[#141021]/90 border-[#3A2F5C] dark:border-[#3A2F5C] text-transparent hover:border-[#16A34A]"}`}>
                <Check className="h-3.5 w-3.5" strokeWidth={3} />
              </button>
            )}
            <div className="p-3 flex flex-col items-center text-center cursor-pointer" onClick={() => sellable && toggleOne(it.id)}>
              <div className="h-16 w-16 rounded-lg overflow-hidden flex items-center justify-center mb-2" style={{ background: meta.color + "14" }}>
                <ItemImage item={{ ...it, icon: Icon }} imgClass="h-16 w-16" iconClass="h-8 w-8" color={meta.color} />
              </div>
              <div className="text-xs text-white dark:text-white font-medium leading-tight line-clamp-2 h-8">{it.name}</div>
              <div className="text-[10px] text-[#9AA0AE] mb-2">{fmtUsd(it.value)} · {meta.label}</div>
              {it.status === "ship_requested" ? (
                <span data-testid="inventory-ship-pending" className="text-[10px] text-[#FBBF24] flex items-center gap-1"><Truck className="h-3 w-3" /> Ship pending</span>
              ) : it.status === "shipped" ? (
                <span className="text-[10px] text-[#0891B2] flex items-center gap-1"><Truck className="h-3 w-3" /> Shipped</span>
              ) : (
                <div className="flex gap-1.5 w-full" onClick={(e) => e.stopPropagation()}>
                  <Button data-testid="inventory-sell-btn" size="sm" onClick={() => act(it.id, "sell")} className="flex-1 h-7 text-[10px] rounded-lg bg-[#16A34A] hover:bg-[#15903f] text-white gap-1"><DollarSign className="h-3 w-3" />Sell</Button>
                  <Button data-testid="inventory-ship-btn" size="sm" variant="outline" onClick={() => setShipTarget(it)} className="flex-1 h-7 text-[10px] rounded-lg border-[#2A2440] text-white gap-1"><Truck className="h-3 w-3" />Ship</Button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
    <WithdrawModal open={!!shipTarget} onOpenChange={(o) => { if (!o) setShipTarget(null); }}
      presetItems={shipTarget ? [shipTarget] : null} presetAction="ship"
      onDone={() => { setShipTarget(null); load(); }} />
    </>
  );
}
